import { useState, useEffect, useRef } from 'react';
import { User, Phone, Mail, Search, Loader2, Bell, AlertTriangle, MapPin } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { workforceService } from '../../../services/workforceService';

export default function AssignmentTab({
  formData,
  onChange,
  errors,
  companyId,
  autoFocusContractorSearch = false
}) {
  const contractorSearchRef = useRef(null);
  const [contractors, setContractors] = useState([]);
  const [isLoadingContractors, setIsLoadingContractors] = useState(false);
  const [contractorSearch, setContractorSearch] = useState('');
  const [showContractorDropdown, setShowContractorDropdown] = useState(false);

  // Get job site contact info
  const jobSiteContact = formData?.jobSite;
  const hasJobSiteContact = !!(jobSiteContact?.contactPerson || jobSiteContact?.contactEmail || jobSiteContact?.contactPhone);

  // Auto-focus contractor search when requested
  useEffect(() => {
    if (autoFocusContractorSearch && contractorSearchRef.current && !formData?.assignedContractor) {
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        contractorSearchRef.current?.focus();
        setShowContractorDropdown(true);
      }, 100);
    }
  }, [autoFocusContractorSearch, formData?.assignedContractor]);

  // Load contractors
  useEffect(() => {
    const loadContractors = async () => {
      if (!companyId) return;

      setIsLoadingContractors(true);
      try {
        const data = await workforceService.getCompanyContractors(companyId);
        setContractors(data || []);
      } catch (err) {
        console.error('Failed to load contractors:', err);
      } finally {
        setIsLoadingContractors(false);
      }
    };

    loadContractors();
  }, [companyId]);

  // Auto-fill contact person when job site changes and useJobSiteContact is true
  useEffect(() => {
    if (formData?.useJobSiteContact && hasJobSiteContact) {
      onChange?.({
        contactPersonName: jobSiteContact?.contactPerson || '',
        contactPersonEmail: jobSiteContact?.contactEmail || '',
        contactPersonPhone: jobSiteContact?.contactPhone || ''
      });
    }
  }, [formData?.jobSiteId, formData?.useJobSiteContact]);

  // Filter contractors by search
  const filteredContractors = contractors?.filter(c =>
    c?.fullName?.toLowerCase()?.includes(contractorSearch?.toLowerCase()) ||
    c?.email?.toLowerCase()?.includes(contractorSearch?.toLowerCase())
  );

  // Handle contractor selection
  const handleSelectContractor = (contractor) => {
    onChange?.({ assignedContractor: contractor });
    setShowContractorDropdown(false);
    setContractorSearch('');
  };

  // Clear contractor selection
  const handleClearContractor = () => {
    onChange?.({ assignedContractor: null });
  };

  // Handle "Use different contact" toggle
  const handleUseJobSiteContactToggle = (checked) => {
    onChange?.({ useJobSiteContact: checked });

    if (checked && hasJobSiteContact) {
      // Auto-fill from job site
      onChange?.({
        contactPersonName: jobSiteContact?.contactPerson || '',
        contactPersonEmail: jobSiteContact?.contactEmail || '',
        contactPersonPhone: jobSiteContact?.contactPhone || ''
      });
    } else if (!checked) {
      // Clear fields for manual entry
      onChange?.({
        contactPersonName: '',
        contactPersonEmail: '',
        contactPersonPhone: ''
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Contractor Assignment - All jobs are private now (no visibility toggle) */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-primary-orange" />
          Assign Contractor
        </h3>

        {formData?.assignedContractor ? (
          // Selected contractor display
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-orange/20 flex items-center justify-center">
                <span className="text-primary-orange font-medium">
                  {formData.assignedContractor?.fullName?.charAt(0)?.toUpperCase() || 'C'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {formData.assignedContractor?.fullName}
                </p>
                <p className="text-xs text-white-shade-2">
                  {formData.assignedContractor?.email}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearContractor}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ) : (
          // Contractor search/select
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
              <input
                ref={contractorSearchRef}
                type="text"
                value={contractorSearch}
                onChange={(e) => setContractorSearch(e?.target?.value)}
                onFocus={() => setShowContractorDropdown(true)}
                placeholder="Search contractors..."
                className={cn(
                  "w-full pl-10 pr-3 py-2 bg-white border rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange",
                  autoFocusContractorSearch ? "border-primary-orange ring-2 ring-primary-orange/30" : "border-gray-300"
                )}
              />
              {isLoadingContractors && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white-shade-2 animate-spin" />
              )}
            </div>

            {/* Dropdown */}
            {showContractorDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredContractors?.length > 0 ? (
                  filteredContractors?.map(contractor => (
                    <button
                      key={contractor?.id}
                      type="button"
                      onClick={() => handleSelectContractor(contractor)}
                      className="w-full px-4 py-3 text-left hover:bg-surface transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      <p className="text-sm text-text-primary">{contractor?.fullName}</p>
                      <p className="text-xs text-white-shade-2">{contractor?.email}</p>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-white-shade-2 text-center">
                    {isLoadingContractors ? 'Loading...' : 'No contractors found'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Notification Notice */}
        {formData?.assignedContractor && (
          <div className="mt-3 p-2 bg-primary-orange/10 rounded flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary-orange" />
            <p className="text-xs text-primary-orange">
              A notification will be sent to this contractor upon job creation
            </p>
          </div>
        )}

        {/* Recurring job warning - contractor applies to first instance only */}
        {formData?.jobType === 'recurring' && formData?.assignedContractor && (
          <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-yellow-400 font-medium">
                First instance only
              </p>
              <p className="text-xs text-yellow-400/80 mt-1">
                This contractor will be assigned to the first scheduled instance only.
                You can assign contractors to other instances from Job Management after creation.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contact Person Section */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3">
          Job Site Contact Person
        </h3>
        <p className="text-xs text-white-shade-2 mb-4">
          Contact details visible to the contractor after job is assigned
        </p>

        {/* Job Site Contact Auto-fill Section */}
        {hasJobSiteContact && (
          <div className="mb-4 p-3 bg-background rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-text-primary">
                  Contact from {formData?.jobSite?.siteName || 'Job Site'}
                </p>
                {jobSiteContact?.contactPerson && (
                  <p className="text-xs text-text-secondary mt-1">
                    {jobSiteContact.contactPerson}
                    {jobSiteContact?.contactPhone && ` | ${jobSiteContact.contactPhone}`}
                    {jobSiteContact?.contactEmail && ` | ${jobSiteContact.contactEmail}`}
                  </p>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-3 pt-3 border-t border-border">
              <input
                type="checkbox"
                checked={!formData?.useJobSiteContact}
                onChange={(e) => handleUseJobSiteContactToggle(!e?.target?.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
              />
              <span className="text-sm text-text-secondary">Use a different contact for this job</span>
            </label>
          </div>
        )}

        {/* Contact Form - Always shown if no job site contact, or if "use different" is checked */}
        {(!hasJobSiteContact || !formData?.useJobSiteContact) && (
          <div className="space-y-3">
            {/* Contact Name */}
            <div>
              <label className="block text-sm font-medium text-white-shade-1 mb-1">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                <input
                  type="text"
                  value={formData?.contactPersonName || ''}
                  onChange={(e) => onChange?.({ contactPersonName: e?.target?.value })}
                  placeholder="Contact person name"
                  className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange"
                />
              </div>
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-1">
                  Phone
                </label>
                <div className="relative flex items-center">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <span className="absolute left-9 top-1/2 -translate-y-1/2 text-text-secondary text-sm">+61</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData?.contactPersonPhone || ''}
                    onChange={(e) => {
                      const value = e?.target?.value?.replace(/[^0-9]/g, '');
                      onChange?.({ contactPersonPhone: value });
                    }}
                    placeholder="412 345 678"
                    className={cn(
                      'w-full pl-[4.5rem] pr-3 py-2 bg-white border rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange',
                      errors?.contactPersonPhone ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                </div>
                {errors?.contactPersonPhone && (
                  <p className="mt-1 text-xs text-red-400">{errors.contactPersonPhone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type="email"
                    value={formData?.contactPersonEmail || ''}
                    onChange={(e) => onChange?.({ contactPersonEmail: e?.target?.value })}
                    placeholder="contact@example.com"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 bg-white border rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange',
                      errors?.contactPersonEmail ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                </div>
                {errors?.contactPersonEmail && (
                  <p className="mt-1 text-xs text-red-400">{errors.contactPersonEmail}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Read-only display when using job site contact */}
        {hasJobSiteContact && formData?.useJobSiteContact && (
          <div className="space-y-2 p-3 bg-accent/10 rounded-lg border border-accent/30">
            <p className="text-xs text-accent font-medium mb-2">Using job site contact:</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              {formData?.contactPersonName && (
                <div className="flex items-center gap-1 text-text-secondary">
                  <User className="w-3 h-3" />
                  <span className="truncate">{formData.contactPersonName}</span>
                </div>
              )}
              {formData?.contactPersonPhone && (
                <div className="flex items-center gap-1 text-text-secondary">
                  <Phone className="w-3 h-3" />
                  <span className="truncate">+61 {formData.contactPersonPhone}</span>
                </div>
              )}
              {formData?.contactPersonEmail && (
                <div className="flex items-center gap-1 text-text-secondary">
                  <Mail className="w-3 h-3" />
                  <span className="truncate">{formData.contactPersonEmail}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Other Details */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Other Details
        </label>
        <textarea
          value={formData?.otherDetails || ''}
          onChange={(e) => onChange?.({ otherDetails: e?.target?.value })}
          placeholder="Any additional information for the contractor (e.g., access codes, parking instructions, special requirements)"
          rows={4}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange resize-none"
        />
      </div>
    </div>
  );
}
